import { Divider, Drawer, Flex } from 'antd';
import { useState, useCallback, useMemo } from 'react';
import styles from './business-hours-permanent.module.scss';
import { BusinessHoursDay, BusinessHoursDayData } from '../business-hours-day/business-hours-day';
import { Button } from '../button/button';
import { BusinessHourItem } from '@/services/business-hours/business-hours.types';
import { useTranslation } from 'react-i18next';

// The API's `weekday` keys. The label shown for each comes from the dictionary.
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

interface BusinessHoursPermanentProps {
  isVisible: boolean;
  onClose: () => void;
  onSave?: (data: BusinessHoursDayData[]) => void;
  isSaveLoading?: boolean;
  permanentHours?: BusinessHourItem[];
}

export function BusinessHoursPermanent({
  isVisible,
  onClose,
  onSave,
  isSaveLoading,
  permanentHours = [],
}: BusinessHoursPermanentProps) {
  const { t } = useTranslation(['businessHours', 'common']);
  const [businessHours, setBusinessHours] = useState<Record<string, BusinessHoursDayData>>({});

  const handleDayChange = useCallback((data: BusinessHoursDayData) => {
    setBusinessHours((prev) => ({
      ...prev,
      [data.weekday]: data,
    }));
  }, []);

  const hasValidationErrors = useMemo(() => {
    return Object.values(businessHours).some((day) => day.hasError);
  }, [businessHours]);

  const handleSave = () => {
    if (hasValidationErrors) return;
    onSave?.(Object.values(businessHours));
  };

  return (
    <Drawer
      className={styles.businessHoursPermanent}
      open={isVisible}
      onClose={onClose}
      destroyOnHidden
      classNames={{
        wrapper: styles.drawerBody,
      }}
      footer={
        <Flex gap={12} justify="flex-end">
          <Button buttonVariant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            loading={isSaveLoading}
            buttonVariant="primary"
            onClick={handleSave}
            disabled={hasValidationErrors}
          >
            {t('common:actions.save')}
          </Button>
        </Flex>
      }
    >
      <p className={styles.permanentSectionTitle}>{t('businessHours:permanent.title')}</p>
      <Divider />

      <Flex vertical>
        {WEEKDAYS.map((weekday) => {
          const dayDataArray = permanentHours.filter((h) => h.wday === weekday);
          const extractTime = (isoString: string) => {
            const match = isoString.match(/T(\d{2}:\d{2})/);
            return match ? match[1] : '';
          };

          const periods =
            dayDataArray.length > 0
              ? dayDataArray.map((dayData) => ({
                  start: dayData.start_time ? extractTime(dayData.start_time) : '08:00',
                  end: dayData.end_time ? extractTime(dayData.end_time) : '16:00',
                }))
              : undefined;

          const firstDayData = dayDataArray[0];

          return (
            <BusinessHoursDay
              key={weekday}
              day={t(`businessHours:weekdays.${weekday}`)}
              weekday={weekday}
              initialIsClosed={
                firstDayData?.is_closed ?? (weekday === 'saturday' || weekday === 'sunday')
              }
              initialIsAllDay={firstDayData?.all_day ?? false}
              initialPeriods={periods}
              onChange={handleDayChange}
            />
          );
        })}
      </Flex>
    </Drawer>
  );
}
